import React, { useEffect, useState } from 'react'
import dayjs from 'dayjs'
import { FiRotateCcw } from 'react-icons/fi'
import styles from '../MySubscription/MySubscription.module.css'
import { API_BASE_URL, getReactivationStatus, reactivateSubscription, getPounceSummary } from '../../Store/apiStore'
import { useCheckoutStore } from '../../Store/checkoutStore'
import { planLabel } from '../../utils/pounceAgentHelpers'
import isEmbeddedCheckoutEnabled from '../../lib/embeddedCheckoutFlag'
import NewModal from '../NewModal/NewModal'
import AnimatedButton from '../AnimatedButton/AnimatedButton'
import PopUp from '../Popup/Popup'
import axios from 'axios'

// Free agents that never had a plan have nothing to reactivate; skip their status call.
const mayHaveEndedPlan = (agent) => {
    if (agent?.agentRole === 'pounce') return false
    const plan = String(agent?.agentPlan || '').trim().toLowerCase()
    const isFreeish = ['', 'free', 'demo'].includes(plan)
    return !isFreeish || Number(agent?.isDeactivated) === 1 || !!agent?.latestSubscription
}

const MySubscription = ({ agents, onChanged }) => {

    const [agentPaygDetails, setagentPaygDetails] = useState()
    // agentId -> reactivation status (only agents that can reactivate)
    const [reactivations, setReactivations] = useState({})
    const [confirmAgent, setConfirmAgent] = useState(null)
    const [reactivateLoading, setReactivateLoading] = useState(false)
    const [popup, setPopup] = useState(null)
    const [pounceData, setPounceData] = useState({})
    const [pounceLoading, setPounceLoading] = useState(true)

    useEffect(() => {
        let cancelled = false
        if (!(agents || []).some((agent) => agent.agentRole === 'pounce')) {
            setPounceData({})
            setPounceLoading(false)
            return
        }
        setPounceLoading(true)
        setPounceData({})
        getPounceSummary()
            .then((data) => { if (!cancelled) setPounceData(data) })
            .catch(() => { if (!cancelled) setPounceData({}) })
            .finally(() => { if (!cancelled) setPounceLoading(false) })
        return () => { cancelled = true }
    }, [agents])

    useEffect(() => {
        let cancelled = false
        const list = (agents || []).filter(mayHaveEndedPlan)
        Promise.all(
            list.map((a) =>
                getReactivationStatus(a.agent_id)
                    .then((s) => [a.agent_id, s])
                    .catch(() => [a.agent_id, null])
            )
        ).then((entries) => {
            if (cancelled) return
            const next = {}
            entries.forEach(([id, s]) => { if (s?.canReactivate) next[id] = s })
            setReactivations(next)
        })
        return () => { cancelled = true }
    }, [agents])

    const handleReactivateConfirmed = async () => {
        const agent = confirmAgent
        const status = agent ? reactivations[agent.agent_id] : null
        if (!agent || !status) return

        // Resume needs a valid card on file; Payment Method is right below on this page.
        if (status.mode === 'resume' && status.needsCard) {
            setConfirmAgent(null)
            setPopup({ type: 'failed', message: 'Please add a card in Payment Method below, then reactivate again.' })
            return
        }

        setReactivateLoading(true)
        try {
            const result = await reactivateSubscription(agent.agent_id, { embedded: isEmbeddedCheckoutEnabled() })
            if (result?.mode === 'checkout' && result?.clientSecret) {
                // Pay in the in-page sheet; on success it lands on the agent page, which confirms it.
                setConfirmAgent(null)
                useCheckoutStore.getState().openCheckout({
                    clientSecret: result.clientSecret,
                    sessionId: result.sessionId,
                    hostedUrl: result.checkoutUrl || null,
                    meta: {
                        agentId: agent.agent_id,
                        planName: status.planName,
                        next: '/fast-agent-detail',
                        extraParams: {
                            businessId: agent?.businessId || agent?.business?.businessId || '',
                            reactivated: '1',
                            session_id: result.sessionId,
                        },
                    },
                })
                return
            }
            if (result?.mode === 'checkout' && result?.checkoutUrl) {
                window.location.href = result.checkoutUrl
                return
            }
            setConfirmAgent(null)
            setReactivations((prev) => {
                const next = { ...prev }
                delete next[agent.agent_id]
                return next
            })
            setPopup({ type: 'success', message: 'Your plan is active again. Your number stays the same.' })
            onChanged?.()
        } catch (error) {
            setConfirmAgent(null)
            setPopup({
                type: 'failed',
                message: error?.code === 'card_required'
                    ? `${error.message} You can add one in Payment Method below.`
                    : error?.message || 'Could not reactivate your plan.',
            })
        } finally {
            setReactivateLoading(false)
        }
    }
    // console.log("agentPaygDetails", agentPaygDetails)
    useEffect(() => {
        const getPaygDetails = async (agentId) => {
            try {
                const res = await axios.post(`${API_BASE_URL}/agent-payg-details`, { agent_id: agentId });
                // console.log("PayG details:", res.dfdfdatvdgfdgfdga);
                setagentPaygDetails(res.data?.data)
                return res.data;
            } catch (error) {
                console.error("Error checking assign number:", error);
                return false;
            }
        };

        // Example: if you already have agents state/prop
        if (agents && agents.length > 0) {
            agents.forEach((agent) => {
                const isPaygPlan = agent?.agentPlan === "Pay-As-You-Go";
                if (isPaygPlan && agent?.agentRole !== 'pounce') {
                    getPaygDetails(agent.agent_id);  // ✅ send only if Pay-As-You-Go
                }
            });
        }
    }, [agents]);



    if (!agents || agents.length === 0) {
        return (
            <div className={styles.cardWrapper}>
                <h3 className={styles.heading}>My Subscriptions</h3>
                <p className={styles.noData}>No subscriptions found.</p>
            </div>
        );
    }

    function formatDate(timestamp) {
        const date = new Date(timestamp);
        const day = String(date.getDate()).padStart(2, '0');
        // "07 Aug 2025" — mahine ka chhota naam (saari dates isi format me).
        const month = date.toLocaleString('en-US', { month: 'short' });
        const year = date.getFullYear();
        return `${day} ${month} ${year}`;
    }

    const currencyMap = {
        USD: "$",
        INR: "₹",
        EUR: "€",
        GBP: "£",
        AUD: "A$",
        CAD: "C$",
    };

    function formatCurrency(amount, currency) {
        const currencyMap = {
            USD: "$",
            INR: "₹",
            EUR: "€",
            GBP: "£",
            AUD: "A$",
            CAD: "C$",
        };

        const symbol = currencyMap[currency?.toUpperCase()] || "";
        const formattedAmount = Number(amount).toLocaleString();

        return `${currency?.toUpperCase() || "USD"} ${symbol}${formattedAmount}`;
    }

    function parseMetadata(metadata) {
        try {
            return typeof metadata === "string" ? JSON.parse(metadata) : metadata;
        } catch (e) {
            console.error("Failed to parse metadata:", e);
            return {};
        }
    }

    // helper (place near your other utils)
    function addDays(date, days) {
        const d = new Date(date);
        if (isNaN(d)) return null;
        d.setDate(d.getDate() + Number(days || 0));
        return d;
    }


    // console.log("agents", agents)





    // One reactivatable agent → button beside the heading; several → one per card (heading can't pick).
    const reactivatableIds = Object.keys(reactivations)
    const headerReactivateAgent = reactivatableIds.length === 1
        ? agents.find((a) => String(a.agent_id) === reactivatableIds[0])
        : null

    return (

        <div className={styles.cardWrapper}>
            <div className={styles.headingRow}>
                <h3 className={styles.heading}>My Subscriptions</h3>
                {headerReactivateAgent && (
                    <button
                        type="button"
                        className={styles.reactivateBtn}
                        onClick={() => setConfirmAgent(headerReactivateAgent)}
                    >
                        <FiRotateCcw className={styles.reactivateIcon} aria-hidden="true" />
                        Reactivate
                    </button>
                )}
            </div>
            {agents?.map((agent) => {
                if (agent?.agentRole === 'pounce') {
                    const summary = pounceData[agent.agent_id]
                    const planName = planLabel(summary?.plan, summary?.deal) || null
                    const fallback = pounceLoading ? 'Loading…' : 'Unavailable'
                    const active = Number(agent.isDeactivated) === 0
                    return (
                        <div key={agent.agent_id} className={styles.MySubscriptionInfo}>
                            <div className={styles.row}>
                                <div className={styles.idDiv}>
                                    <p className={styles.label1}>Agent Code:</p>
                                    <p className={styles.valueBold}>{agent.agentCode || 'NA'}</p>
                                </div>
                                <div className={styles.statusDiv}>
                                    <div className={`${styles.Dot} ${active ? styles.activeDot : styles.deactivatedDot}`} />
                                    <div className={`${styles.status} ${active ? styles.activeStatus : styles.deactivatedStatus}`}>
                                        {active ? 'Active' : 'Inactive'}
                                    </div>
                                </div>
                            </div>
                            <div className={styles.row2}>
                                <span className={styles.label}>Agent Type</span>
                                <span className={styles.value}>Outbound Caller</span>
                            </div>
                            <div className={styles.row2}>
                                <span className={styles.label}>Agent Name</span>
                                <span className={styles.value}>{agent.agentName || 'NA'}</span>
                            </div>
                            <div className={styles.row2}>
                                <span className={styles.label}>Business Name</span>
                                <span className={styles.value}>{agent.business?.businessName || 'NA'}</span>
                            </div>
                            <div className={styles.row2}>
                                <span className={styles.label}>Outbound Caller Plan</span>
                                <span className={styles.plan}>{planName || fallback}</span>
                            </div>
                            <div className={styles.row2}>
                                <span className={styles.label}>Booked This Month</span>
                                <span className={styles.value}>{summary?.bookedThisMonth ?? fallback}</span>
                            </div>
                        </div>
                    )
                }
                const sub = agent?.latestSubscription;
                const meta = parseMetadata(sub?.metadata);
                const deferDays = Number(meta?.defer_days) || 0;



                // ✅ Deferred plan condition
                const isDeferredPlan =
                    sub?.status?.toLowerCase() === 'active' && sub?.subscription_id === 'byAdmin' && agent?.agentPlan !== "Pay-As-You-Go" &&agent?.agentPlan !== "free";

                const isPaygPlan = agent?.agentPlan === "Pay-As-You-Go"
                // && agent?.latestSubscription?.status ==="inactive"
                // AppSumo outranks the free check: redemption grants minutes and
                // sets userType='paid' on the account, but never touches the
                // agent's agentPlan, which stays "free". Reading that literally
                // labelled a paying AppSumo customer "Free".
                const isAppsumoPlan = Boolean(agent?.appsumoTier)
                const isFreePlan = agent?.agentPlan === "free" && !isAppsumoPlan


                const deferredStartDate = sub?.current_period_start ? new Date(sub.current_period_start) : null;


                const deferredEndDate = deferredStartDate ? addDays(deferredStartDate, deferDays) : null;

                return (
                    <div key={agent.agent_id} className={styles.MySubscriptionInfo}>
                        <div className={styles.row}>
                            <div className={styles.idDiv}>
                                <p className={styles.label1}>Agent Code:</p>
                                <p className={styles.valueBold}>{agent.agentCode ? agent.agentCode : "Na"}</p>
                            </div>
                            {/* <div className={styles.statusDiv}>
                        <div className={styles.Dot}></div>
                        <div className={styles.status}>{agent.isDeactivated === 0 ? "Active" : "Deactivated"}</div>
                    </div> */}
                            <div className={styles.statusDiv}>
                                <div
                                    className={`${styles.Dot} ${agent.isDeactivated === 0 ? styles.activeDot : styles.deactivatedDot
                                        }`}
                                ></div>
                                <div
                                    className={`${styles.status} ${agent.isDeactivated === 0 ? styles.activeStatus : styles.deactivatedStatus
                                        }`}
                                >
                                    {agent.isDeactivated === 0 ? "Active" : "Inactive"}
                                </div>
                            </div>

                        </div>

                        <div className={styles.row2}>
                            <span className={styles.label}>Agent Name</span>
                            <span className={styles.value}>{agent?.agentName ? agent.agentName : "NA"}</span>
                        </div>
                        <div className={styles.row2}>
                            <span className={styles.label}>Business Name</span>
                            <span className={styles.value}>{agent?.business?.businessName ? agent?.business?.businessName : "NA"}</span>
                        </div>
                        <div className={styles.row2}>
                            <span className={styles.label}>Plan Activated</span>
                            <span className={styles.plan}>
                                {isAppsumoPlan
                                    ? agent?.appsumoPlanName || `AppSumo Tier ${agent.appsumoTier}`
                                    : isFreePlan
                                        ? "Free"
                                        : isPaygPlan
                                            ? "Pay-As-You-Go"
                                            : isDeferredPlan
                                                ? "Deferred"
                                                : agent?.latestSubscription?.plan_name || "Free"}
                            </span>
                        </div>
                        <div className={styles.row2}>
                            <span className={styles.label}>
                                {isPaygPlan ? "Minutes Used" : "Minutes Remaining"}
                            </span>
                            <span className={styles.value}>
                                {isPaygPlan
                                    ? agentPaygDetails?.total_mins ?? "NA"
                                    : agent?.mins_left
                                        ? Math.floor(agent.mins_left / 60)
                                        : "NA"}
                            </span>
                        </div>

                        {isDeferredPlan ? (
                            <>
                                <div className={styles.row2}>
                                    <span className={styles.label}>Deferred Start Date</span>
                                    <span className={styles.value}>
                                        {agent?.latestSubscription?.current_period_start
                                            ? formatDate(agent.latestSubscription.current_period_start)
                                            : "NA"}
                                    </span>
                                </div>

                                <div className={styles.row2}>
                                    <span className={styles.label}>Deferred End Date</span>
                                    <span className={styles.value}>
                                        {deferredEndDate ? formatDate(deferredEndDate) : "NA"}
                                    </span>
                                </div>
                            </>
                        ) : isPaygPlan ? (() => {
                            /*
                             * PAYG ($5 trial pack) has no refund entitlement, so a cancel
                             * is always scheduled: minutes and number keep working to the
                             * end of the paid period. From that point the date below is a
                             * last-day-of-service, not a billing date — same rule as the
                             * SMB card further down.
                             *
                             * `access_until` comes from the backend and is Stripe's own end
                             * date (cancel_at / current_period_end), falling back to
                             * period_start + 21 days. It is preferred over `period_end`
                             * because that column is stored as purchase + 30 days while the
                             * Stripe trial runs 21 — showing it would promise 9 days that
                             * do not exist. `period_end` stays as the last fallback so an
                             * older backend (no access_until in the payload) renders
                             * exactly as it does today.
                             */
                            const paygCancelled =
                                String(agentPaygDetails?.status || "").toLowerCase() !== "active" ||
                                String(agent?.latestSubscription?.status || "").toLowerCase() === "cancel_scheduled";
                            const paygDate =
                                agentPaygDetails?.access_until || agentPaygDetails?.period_end;
                            return (
                                <>
                                    <div className={styles.row2}>
                                        <span className={styles.label}>Initial Purchase Date</span>
                                        <span className={styles.value}>{agentPaygDetails?.period_start ? formatDate(agentPaygDetails?.period_start) : "Na"}</span>
                                    </div>
                                    <div className={styles.row2}>
                                        <span className={styles.label}>
                                            {paygCancelled ? "Valid Till" : "Next Billing Date"}
                                        </span>
                                        <span className={styles.valuebilling}>{paygDate ? formatDate(paygDate) : "Na"}</span>
                                    </div>
                                </>
                            );
                        })()
                        // AppSumo joins the free branch: the tier is a one-time
                        // grant with no Stripe subscription behind it, so the
                        // recurring billing rows below would all render "Na".
                        : isFreePlan || isAppsumoPlan ? (<></>) :
                            (
                                <>
                                    {(() => {
                                        const meta = parseMetadata(agent?.latestSubscription?.metadata);
                                        const isTrialSub =
                                            meta?.trailPack === "yes" ||
                                            agent?.latestSubscription?.status === "trialing";
                                        // For "Billed Today" prefer amount_paid_today (added by
                                        // the webhook fix). For trial subs it's the activation
                                        // fee ($20); plan_unit_amount may now hold the recurring
                                        // price after the backend update so we don't want to
                                        // display that as "billed today".
                                        const billedAmount =
                                            meta?.amount_paid_today != null
                                                ? meta.amount_paid_today
                                                : agent?.latestSubscription?.plan_unit_amount;
                                        const recurringAmount =
                                            meta?.original_plan_amount;
                                        // Trial subs are fixed at 21 days ($20 Starter activation).
                                        // Backend's next_renewal_date can come back as ~1 month
                                        // out (the regular billing cycle anchor), so for trial
                                        // subs we derive Trial Ends / First Bill from
                                        // current_period_start + trial_days (default 21).
                                        const trialDays = Number(meta?.trial_days) || 21;
                                        const trialEndsDate =
                                            isTrialSub && agent?.latestSubscription?.current_period_start
                                                ? addDays(
                                                      agent.latestSubscription.current_period_start,
                                                      trialDays
                                                  )
                                                : null;
                                        const billingDateToShow = isTrialSub
                                            ? trialEndsDate
                                            : agent?.latestSubscription?.next_renewal_date;
                                        /*
                                         * Cancelled, but still inside the paid period.
                                         *
                                         * When a cancellation is not refund-eligible the /refund
                                         * controller does NOT tear the plan down — it sets Stripe's
                                         * cancel_at_period_end, and handleSubscriptionCanceled
                                         * (stripeModel) writes the row as:
                                         *     status:         'cancel_scheduled'
                                         *     next_renewal_date: <cancel date>
                                         *     payment_status: 'payment_canceled_on_end'
                                         *
                                         * So the date in this row is no longer a date the customer
                                         * will be charged on — it is the last day the agent, its
                                         * number and its minutes keep working. Calling it "Next
                                         * Billing Date" tells a cancelled customer they are about
                                         * to be billed again, which is the opposite of the truth.
                                         *
                                         * LABEL ONLY. The date itself is already correct in both
                                         * cases and is deliberately left alone:
                                         *   - trial pack ($20/21-day)  → current_period_start + 21d
                                         *   - SMB monthly / yearly     → next_renewal_date, which
                                         *     the webhook already overwrote with the cancel date.
                                         * Nothing about how a cancellation is decided or processed
                                         * is touched here.
                                         *
                                         * Both markers are checked: `status` is the primary signal,
                                         * `payment_status` covers a row whose status was later moved
                                         * on by another webhook while the scheduled cancel stands.
                                         */
                                        const subStatus = String(
                                            agent?.latestSubscription?.status || ""
                                        ).toLowerCase();
                                        const isCancelScheduled =
                                            subStatus === "cancel_scheduled" ||
                                            agent?.latestSubscription?.payment_status ===
                                                "payment_canceled_on_end";
                                        const billingLabel = isCancelScheduled
                                            ? "Valid Till"
                                            : isTrialSub
                                                ? "Trial Ends / First Bill"
                                                : "Next Billing Date";
                                        return (
                                            <>
                                                <div className={styles.row2}>
                                                    <span className={styles.label}>Initial Purchase Date</span>
                                                    <span className={styles.value}>{agent?.latestSubscription?.current_period_start ? formatDate(agent?.latestSubscription?.current_period_start) : "Na"}</span>
                                                </div>
                                                <div className={styles.row2}>
                                                    <span className={styles.label}>
                                                        {billingLabel}
                                                    </span>
                                                    <span className={styles.valuebilling}>{billingDateToShow ? formatDate(billingDateToShow) : "Na"}</span>
                                                </div>

                                                <div className={styles.row2}>
                                                    <span className={styles.label}>Frequency & Price</span>
                                                    {agent?.latestSubscription && (
                                                        <span className={styles.frequencyprice}>
                                                            {formatCurrency(
                                                                recurringAmount,
                                                                agent.latestSubscription.plan_currency
                                                            )}{" "}
                                                            / {agent.latestSubscription.interval}
                                                            {isTrialSub ? " (after trial)" : ""}
                                                        </span>
                                                    )}
                                                </div>
                                                <div className={styles.row2}>
                                                    <span className={styles.label}>
                                                        {isTrialSub ? "Activation Paid" : "Billed Date & Price:"}
                                                    </span>
                                                    {agent?.latestSubscription && (
                                                        <span className={styles.frequencyprice}>
                                                            {formatCurrency(
                                                                billedAmount,
                                                                agent.latestSubscription.plan_currency
                                                            )}{" "}
                                                            on {formatDate(agent.latestSubscription.current_period_start)}
                                                        </span>
                                                    )}
                                                </div>
                                            </>
                                        );
                                    })()}
                                </>
                            )}



                        {/* ✅ New row for Deferred Plan */}
                        {isDeferredPlan && (
                            <div className={styles.deferredNote}>
                                <span>
                                    📅 Plan is <strong>deferred</strong>
                                    {typeof meta?.defer_days === "number" && meta.defer_days > 0
                                        ? ` and ends in ${meta.defer_days} days`
                                        : ""}
                                    .
                                </span>
                            </div>
                        )}
                        {isPaygPlan && (
                            <div className={styles.deferredNote}>
                                <span>📅 Your plan is <strong>Pay-As-You-Go</strong>.</span>
                            </div>
                        )}

                        {reactivations[agent.agent_id] && !headerReactivateAgent && (() => {
                            const r = reactivations[agent.agent_id]
                            const endDate = r.nextChargeDate ? dayjs(r.nextChargeDate).format('D MMM YYYY') : null
                            return (
                                <div className={styles.reactivateBar}>
                                    <div className={styles.reactivateText}>
                                        <span className={styles.reactivateHeadline}>
                                            {r.mode === 'resume' ? 'Subscription cancelled' : 'Subscription ended'}
                                        </span>
                                        <span className={styles.reactivateSub}>
                                            {r.mode === 'resume'
                                                ? `Service continues${endDate ? ` until ${endDate}` : ' until the period ends'}. Reactivate to keep it running.`
                                                : 'Reactivate to get this agent live again.'}
                                        </span>
                                    </div>
                                    <button
                                        type="button"
                                        className={styles.reactivateBtn}
                                        onClick={() => setConfirmAgent(agent)}
                                    >
                                        <FiRotateCcw className={styles.reactivateIcon} aria-hidden="true" />
                                        Reactivate
                                    </button>
                                </div>
                            )
                        })()}


                    </div>
                )
            })}

            {confirmAgent && reactivations[confirmAgent.agent_id] && (() => {
                const r = reactivations[confirmAgent.agent_id]
                const isResume = r.mode === 'resume'
                const chargeDate = r.nextChargeDate ? dayjs(r.nextChargeDate).format('D MMM YYYY') : null
                const cardText = r.card?.last4 ? `your ${r.card.brand || 'card'} ending ${r.card.last4}` : 'your saved card'
                const planLabel = r.isPayg
                    ? r.hasMonthlyNumberFee ? 'Pay-As-You-Go + $5 number plan' : 'Pay-As-You-Go plan'
                    : `${r.planName || 'previous'} plan`
                return (
                    <NewModal
                        isOpen
                        onClose={() => !reactivateLoading && setConfirmAgent(null)}
                        title={null}
                        height="auto"
                    >
                        <div className={styles.reactivateModal}>
                            <h2 className={styles.reactivateTitle}>Reactivate Subscription?</h2>
                            <p className={styles.reactivateDesc}>
                                {isResume
                                    ? r.needsCard
                                        ? `Your ${planLabel} will continue. Please add a card first — it will be charged${chargeDate ? ` on ${chargeDate}` : ' on the renewal date'}.`
                                        : `Your ${planLabel} will continue with the same number. Nothing is charged now; ${cardText} will be charged${chargeDate ? ` on ${chargeDate}` : ' on the renewal date'}.`
                                    : `Your ${planLabel} will start again. You'll pay now on the next screen, then choose a new number for your agent.`}
                            </p>
                            <AnimatedButton
                                label={
                                    isResume && r.needsCard
                                        ? 'Add Card'
                                        : isResume ? 'Yes, Reactivate' : 'Continue to Payment'
                                }
                                isLoading={reactivateLoading}
                                disabled={reactivateLoading}
                                onClick={handleReactivateConfirmed}
                                positionType="relative"
                                width="100%"
                                bottom="0"
                            />
                        </div>
                    </NewModal>
                )
            })()}

            {popup && (
                <PopUp
                    type={popup.type}
                    message={popup.message}
                    onClose={() => setPopup(null)}
                />
            )}

        </div>

    )
}

export default MySubscription
